import React, { useState, useMemo } from "react";
import { DataTable, Badge, Pagination } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { Eye, Edit2, UserX } from "lucide-react";

export const MaterialExpensesTable = ({ expenses, onView, onEdit, onToggleDisable }) => {
  const [page, setPage] = useState(1);
  const limit = 10;

  const columns = [
    // {
    //   header: "Expense ID",
    //   accessor: (row) => (
    //     <div className="text-brand-orange font-bold !text-[10px] flex items-center py-2 uppercase tracking-tight">
    //       #{row.expenseId || "—"}
    //     </div>
    //   ),
    //   minWidth: "110px",
    // },
    {
      header: "Date",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] flex items-center">
          {row.date ? fmtDate(row.date) : "—"}
        </div>
      ),
      minWidth: "90px",
    },
    {
      header: "Verve ID",
      accessor: (row) => (
        <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
          {row.project?.customId || row.project?.customerCustomId || row.project?.projectId || row.projectId || "—"}
        </span>
      ),
      minWidth: "100px",
    },
    {
      header: 'Customer Info',
      accessor: (row) => {
        const p = row.project || {};
        const fullName = p.customerName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || row.clientName || 'No Name';
        const phone = p.phoneNumber || p.phone || '—';
        const email = p.email || '';
        const contactStr = email ? `${phone} | ${email}` : phone;
        
        return (
          <div className="flex flex-col min-w-0" title={`${fullName}\n${contactStr}`}>
            <span className="text-brand-dark tracking-tight text-sm truncate" title={fullName}>{fullName}</span>
            <span className="text-[10px] text-slate-400 truncate mt-0.5" title={contactStr}>
              {phone}{email && <span className="mx-1 text-slate-300">|</span>}{email}
            </span>
          </div>
        );
      },
      minWidth: '200px',
    },
    {
      header: 'Address',
      accessor: (row) => {
        const p = row.project || {};
        const parts = [
          p.addressLine1 || p.address1,
          p.addressLine2 || p.address2,
          p.city,
          p.district,
          p.state,
          p.pincode
        ].filter(Boolean);
        const addressText = parts.length > 0 ? parts.join(", ") : '—';
        return (
          <div className="flex flex-col min-w-0" title={addressText}>
            <span className="text-slate-600 text-[11px] truncate">{addressText}</span>
          </div>
        );
      },
      minWidth: '200px',
    },
    {
      header: "Material Name",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] font-medium flex items-center truncate max-w-[130px]" title={row.materialName}>
          {row.materialName || "—"}
        </div>
      ),
      minWidth: "130px",
    },
    {
      header: "Category",
      accessor: (row) => (
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-brand-blue bg-blue-50 border border-blue-100">
          {row.category || "—"}
        </span>
      ),
      minWidth: "110px",
    },
    {
      header: "Quantity",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] font-bold flex items-center">
          {row.quantity || "—"}
        </div>
      ),
      minWidth: "80px",
    },
    // {
    //   header: "Rate",
    //   accessor: (row) => (
    //     <div className="text-slate-500 !text-[10px] font-medium flex items-center">
    //       {fmt(row.rate || 0)}
    //     </div>
    //   ),
    //   minWidth: "100px",
    // },
    {
      header: "Amount",
      accessor: (row) => (
        <div className="text-rose-500 !text-[10px] font-bold flex items-center">
          {fmt(row.amount || 0)}
        </div>
      ),
      minWidth: "110px",
    },
    {
      header: "Supplier",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] font-medium flex items-center truncate max-w-[120px]" title={row.supplier}>
          {row.supplier || "—"}
        </div>
      ),
      minWidth: "120px",
    },
    // {
    //   header: "Invoice No",
    //   accessor: (row) => (
    //     <div className="text-slate-500 !text-[10px] font-medium flex items-center">
    //       {row.invoiceNo || "—"}
    //     </div>
    //   ),
    //   minWidth: "100px",
    // },
    {
      header: "Status",
      accessor: (row) => (
        <Badge status={row.status || "pending"} />
      ),
      minWidth: "100px",
    },
    {
      header: "Actions",
      accessor: (row) => {
        const isCancelled = row.status === 'cancelled';
        return (
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => { e.stopPropagation(); onView && onView(row); }}
              className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
              title="View Detail"
            >
              <Eye size={16} />
            </button>
          </div>
        );
      },
      minWidth: "60px",
    }
  ];

  const getRowClassName = (row) => {
    return row.status === 'cancelled' ? "opacity-50 grayscale saturate-0" : "";
  };

  const safeExpenses = expenses || [];
  const totalPages = Math.ceil(safeExpenses.length / limit);
  const paginatedExpenses = useMemo(() => {
    const start = (page - 1) * limit;
    return safeExpenses.slice(start, start + limit);
  }, [safeExpenses, page]);

  // Handle case where items are deleted and page goes out of bounds
  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-700 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col">
      <DataTable 
        columns={columns} 
        data={paginatedExpenses} 
        getRowClassName={getRowClassName}
      />
      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};
